import type { ReactElement } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRoutes } from './routes.js';
import { AuthProvider } from './lib/auth/AuthProvider.js';
import { ToastProvider } from './components/feedback/index.js';
import { TooltipProvider } from './components/overlay/index.js';
import { shouldRetry } from './lib/api/client.js';
import { hasSession, subscribeToAuth } from './lib/api/auth.js';

/**
 * Retry is DERIVED from the response's `retryable` flag rather than guessed,
 * and a 409 is never retried — §17.5 makes it "already done", not "try again".
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    // A command carries an Idempotency-Key, but a blind retry would still be a
    // second decision by the user. Retries are explicit, per mutation.
    mutations: { retry: false },
  },
});

/**
 * THE CACHE GOES WHEN THE SESSION GOES.
 *
 * Signing out cleared the tokens and nothing else. That stops the NEXT request
 * and does nothing about what this client is already holding, and the TeraAgo
 * query keys are `['cityload', ...]` with no account in them — `partner/api.ts`
 * shares that prefix on purpose. So the next person to sign in on the same tab
 * was painted the previous operator's bookings, wallets, settlements and driver
 * details out of cache, before their own refetch landed.
 *
 * Half the places this console was built for are a shared desk in an office.
 * Both shells say so in their own comments, and neither had anything behind it.
 *
 * HUNG OFF THE TOKEN STORE, not off the sign-out button, because there is more
 * than one way to lose a session: the button, and a refresh token that stops
 * working. `clearTokens()` is the single point both pass through.
 *
 * `hasSession()` IS THE TEST, and it is not optional. The store notifies on
 * `setTokens` as well, and the silent token refresh in `client.ts` calls
 * `setTokens` every time it renews — clearing on every notification would empty
 * the whole cache every fifteen minutes and refetch the console from scratch.
 * A notification with no session left is a sign-out; anything else is not.
 *
 * Module scope, once, for the life of the tab. There is one client and one
 * token store, so a per-component subscription would be several listeners
 * doing the same thing.
 */
subscribeToAuth(() => {
  if (!hasSession()) queryClient.clear();
});

export function App(): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <TooltipProvider delayDuration={150}>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </TooltipProvider>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
